import { SHARE_CLIENT_MODE_MATRIX } from '../../../src/sharePolicy/contracts'
import type {
  OriginalCopy,
  PreparedCopy,
  SharePolicyClientDto,
} from '../../../src/sharePolicy/contracts'
import type { DownloadMode } from '../types'

export { readClientPolicyDto } from '../../../src/sharePolicy/clientDto'

/**
 * Resolve manually-created/older manifests through the same single adapter.
 * Network manifests always have `policy`; the fallback keeps persisted/test
 * manifests from creating a second permission vocabulary.
 */
export function clientPolicyForManifest(input: {
  policy?: SharePolicyClientDto
  picksEnabled?: boolean
  downloadEnabled?: DownloadMode
  expiresAt: string
}): SharePolicyClientDto {
  return (
    input.policy ??
    adaptLegacyManifestPolicy({
      picksEnabled: input.picksEnabled,
      downloadEnabled: input.downloadEnabled,
      expiresAt: Date.parse(input.expiresAt),
    })
  )
}

/**
 * The only compatibility bridge for a pre-policy manifest. Legacy fields are
 * fallback inputs solely when the Worker omitted `policy`; the resulting
 * object uses the frozen Phase 5 vocabulary everywhere downstream.
 */
export function adaptLegacyManifestPolicy(input: {
  picksEnabled?: boolean
  downloadEnabled?: DownloadMode
  expiresAt: number
}): SharePolicyClientDto {
  const picksEnabled = input.picksEnabled ?? true
  const downloadEnabled = input.downloadEnabled ?? 'off'
  const mode = picksEnabled ? 'select' : downloadEnabled === 'off' ? 'view' : 'approve'
  const prepared: readonly PreparedCopy[] =
    downloadEnabled === 'off' ? [] : downloadEnabled === 'web' ? ['web'] : ['largest']
  const originals: readonly OriginalCopy[] =
    downloadEnabled === 'hr' || downloadEnabled === 'original' ? ['jpg', 'raw'] : []

  return Object.freeze({
    policyVersion: 0,
    access: 'anyone',
    passcodeRequired: false,
    can: SHARE_CLIENT_MODE_MATRIX[mode],
    downloads: Object.freeze({
      prepared: Object.freeze(prepared),
      originals: Object.freeze(originals),
    }),
    expiresAt: input.expiresAt,
  })
}
